import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import cn from 'classnames';

import { useNotificationsCtx } from '../../model';
import { useUrgentNotificationsCounter } from '../../model/use-urgent-notifications-counter';
import Notification from '../notification';
import UnreadNotificationsCounter from '../unread-notifications-counter/unread-notifications-counter';
import NotificationsEmpty from '../notifications-empty';
import classes from './notifications-list.module.scss';

type NotificationsListProps = {
  className?: string;
};

const Container = ({ children }: { children: React.ReactNode }) => (
  <Stack alignItems='flex-end' direction='column' spacing='10px' padding='0 10px'>
    {children}
  </Stack>
);

const EmptyBar = () => <li style={{ minHeight: 8, margin: 0 }} />;

const NotificationsList = ({ className }: NotificationsListProps) => {
  const {
    activeNotification,
    allNotifications,
    notificationType,
    isNotificationsLoading,
    deactivateNotification,
    markAsRead,
    closeNotifications,
    openAllNotifications
  } = useNotificationsCtx();

  const { totalUrgentNotifications } = useUrgentNotificationsCounter();

  if (isNotificationsLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', padding: '10px', gap: '10px' }}>
        <Skeleton variant='text' width='100%' height={90} sx={{ transform: 'none' }} />
        <Skeleton variant='text' width='100%' height={90} sx={{ transform: 'none' }} />
      </div>
    );
  }

  if (allNotifications.length === 0) {
    return <NotificationsEmpty />;
  }

  const isUrgent = notificationType === 'urgent';
  const notifications = isUrgent ? (activeNotification ? [activeNotification] : []) : allNotifications;

  const calculateRemainingUrgentCount = (totalCount: number) => {
    const CURRENTLY_DISPLAYED_COUNT = 1;
    return Math.max(totalCount - CURRENTLY_DISPLAYED_COUNT, 0);
  };

  const total = calculateRemainingUrgentCount(totalUrgentNotifications);

  const Root = isUrgent ? Container : PerfectScrollbar;

  const handleClickCounter = () => {
    closeNotifications();
    openAllNotifications();
  };

  return (
    <Root>
      <ul
        className={cn(
          classes['notifications-list'],
          {
            [classes['notifications-list--urgent']]: isUrgent
          },
          className
        )}
      >
        {!isUrgent && <EmptyBar />}
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            notification={notification}
            isUrgent={isUrgent}
            onDeactivate={deactivateNotification}
            onClose={closeNotifications}
            onMarkAsRead={markAsRead}
          />
        ))}
        {!isUrgent && <EmptyBar />}
      </ul>

      {isUrgent && !!total && <UnreadNotificationsCounter count={total} onClick={handleClickCounter} />}
    </Root>
  );
};

export default NotificationsList;
